import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useState } from "react";
import LoadingButton from "../../components/LoadingButton";
import { COMPANY_SIZES, ICP_FIELD_LIMITS, ICP_TYPES, ICP_TYPE_B2B } from "../../constants";
import { Company } from "../../types";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`icp-tabpanel-${index}`}
      aria-labelledby={`icp-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

interface IcpFormProps {
  open: boolean;
  onClose: () => void;
  companyId?: string;
  company?: Company;
  onSave?: (formData: any) => void;
  existingData?: any;
  loading?: boolean;
}

export default ({
  open,
  onClose,
  companyId,
  company,
  onSave,
  existingData,
  loading = false,
}: IcpFormProps) => {
  const [tabValue, setTabValue] = useState(0);
  // Create a default form data structure
  const defaultFormData = {
    name: "",
    companySize: "",
    industry: "",
    budget: "",
    location: "",
    description: "",
    // Organization Details
    businessProfile: {
      icpType: ICP_TYPE_B2B,
      industries: [],
      companySizeRange: "",
      revenue: "",
      geography: "",
      businessCharacteristics: [],
    },
    strategicContext: {
      growthStage: "",
      priorities: [],
      values: [],
    },
    challenges: {
      painPoints: [],
      needs: [],
    },
    buyingProcess: {
      decisionMakers: [],
      evaluationCriteria: [],
      objections: [],
    },
    // Personas sections
    personas: [
      {
        name: "",
        role: "",
        profile: "",
        challenges: [],
        motivations: [],
      },
    ],
    // Marketing recommendations
    goToMarket: {
      channels: [],
      messaging: [],
      contentStrategies: [],
    },
  };

  // Effect to update form data when existingData changes
  const [formData, setFormData] = useState<any>(defaultFormData);

  // Update form data when existingData changes
  React.useEffect(() => {
    if (!existingData) {
      setFormData(defaultFormData);
      return;
    }

    // Deep merge the existing data with defaults to ensure all fields exist
    const updatedFormData = {
      ...defaultFormData,
      ...existingData,
      // Handle nested objects carefully
      businessProfile: {
        ...defaultFormData.businessProfile,
        ...(existingData.businessProfile || {}),
        // Ensure icpType has a default
        icpType: existingData.businessProfile?.icpType || ICP_TYPE_B2B,
        // Ensure arrays are properly initialized
        industries: existingData.businessProfile?.industries || [],
        businessCharacteristics:
          existingData.businessProfile?.businessCharacteristics || [],
      },
      strategicContext: {
        ...defaultFormData.strategicContext,
        ...(existingData.strategicContext || {}),
        priorities: existingData.strategicContext?.priorities || [],
        values: existingData.strategicContext?.values || [],
      },
      challenges: {
        ...defaultFormData.challenges,
        ...(existingData.challenges || {}),
        painPoints: existingData.challenges?.painPoints || [],
        needs: existingData.challenges?.needs || [],
      },
      buyingProcess: {
        ...defaultFormData.buyingProcess,
        ...(existingData.buyingProcess || {}),
        // Handle complex decision makers format - convert objects to strings if needed
        decisionMakers: Array.isArray(
          existingData.buyingProcess?.decisionMakers
        )
          ? existingData.buyingProcess.decisionMakers.map((dm: any) =>
              typeof dm === "string"
                ? dm
                : dm.role || dm.title || dm.name || JSON.stringify(dm)
            )
          : [],
        evaluationCriteria:
          existingData.buyingProcess?.evaluationCriteria || [],
        objections: existingData.buyingProcess?.objections || [],
      },
      personas: existingData.personas?.length
        ? existingData.personas.map((p: any) => ({
            name: p.name || p.title || "",
            role: p.role || "",
            profile: p.profile || "",
            challenges: p.challenges || [],
            motivations: p.motivations || p.concerns || [],
          }))
        : defaultFormData.personas,
      goToMarket: {
        ...defaultFormData.goToMarket,
        ...(existingData.goToMarket || {}),
        channels: existingData.goToMarket?.channels || [],
        messaging: existingData.goToMarket?.messaging || [],
        contentStrategies:
          existingData.goToMarket?.content ||
          existingData.goToMarket?.contentStrategies ||
          [],
      },
    };

    setFormData(updatedFormData);
  }, [existingData]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSubmit = () => {
    const newIcp = {
      id: existingData?.id || null,
      companyId: companyId,
      ...formData,
    };

    if (onSave) {
      onSave(newIcp);
    }

    onClose();
  };

  const handleInputChange = (event: any) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const handleNestedChange = (section: string, field: string, value: any) => {
    setFormData({
      ...formData,
      [section]: {
        ...formData[section],
        [field]: value,
      },
    });
  };

  const handleArrayInput = (section: string, field: string, event: any) => {
    // Convert comma-separated string to array
    const value = event.target.value
      .split(",")
      .map((item: string) => item.trim());
    handleNestedChange(section, field, value);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {existingData ? "Edit" : "New"} Ideal Customer Profile
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="icp tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab label="Basic Info" />
            <Tab label="Organization Details" />
            <Tab label="Challenges & Buying" />
            <Tab label="Personas" />
            <Tab label="Go-to-Market" />
          </Tabs>
        </Box>

        {/* Basic Info Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              name="name"
              label="Profile Name"
              value={formData.name}
              onChange={handleInputChange}
              fullWidth
              required
              inputProps={{ maxLength: ICP_FIELD_LIMITS.name }}
              helperText={`${formData.name?.length || 0}/${ICP_FIELD_LIMITS.name} characters`}
            />

            <FormControl fullWidth>
              <InputLabel>ICP Type</InputLabel>
              <Select
                value={formData.businessProfile?.icpType || ICP_TYPE_B2B}
                label="ICP Type"
                onChange={(e) =>
                  handleNestedChange("businessProfile", "icpType", e.target.value)
                }
              >
                {ICP_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              {formData.businessProfile?.icpType === "b2c" ? "Basic Consumer Profile" : "Basic Company Profile"}
            </Typography>
            <Divider />

            {formData.businessProfile?.icpType === "b2c" ? (
              <TextField
                name="companySize"
                value={formData.companySize}
                label="Demographics"
                onChange={handleInputChange}
                fullWidth
                placeholder="e.g., 25-45 years old, middle to upper income"
                inputProps={{ maxLength: ICP_FIELD_LIMITS.companySize }}
                helperText={`${formData.companySize?.length || 0}/${ICP_FIELD_LIMITS.companySize} characters`}
              />
            ) : (
              <FormControl fullWidth>
                <InputLabel>Company Size Range</InputLabel>
                <Select
                  name="companySize"
                  value={formData.companySize}
                  label="Company Size Range"
                  onChange={handleInputChange}
                >
                  {COMPANY_SIZES.map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              name="industry"
              value={formData.industry}
              label={formData.businessProfile?.icpType === "b2c" ? "Market Segment" : "Industry"}
              onChange={handleInputChange}
              placeholder={formData.businessProfile?.icpType === "b2c"
                ? "e.g., Health & wellness, Home improvement, Luxury goods"
                : "e.g., Hospitality, High-end retail, Professional services"}
              inputProps={{ maxLength: ICP_FIELD_LIMITS.industry }}
              helperText={`${formData.industry?.length || 0}/${ICP_FIELD_LIMITS.industry} characters`}
            />

            <TextField
              name="budget"
              label={formData.businessProfile?.icpType === "b2c" ? "Spending Range" : "Budget Range"}
              value={formData.budget}
              onChange={handleInputChange}
              fullWidth
              placeholder={formData.businessProfile?.icpType === "b2c"
                ? "e.g., $50-$200 per purchase"
                : "e.g., $1,000-$5,000/month"}
              inputProps={{ maxLength: ICP_FIELD_LIMITS.budget }}
              helperText={`${formData.budget?.length || 0}/${ICP_FIELD_LIMITS.budget} characters`}
            />

            <TextField
              name="location"
              label="Geographic Location"
              value={formData.location}
              onChange={handleInputChange}
              fullWidth
              placeholder="e.g., San Francisco, CA and surrounding areas"
              inputProps={{ maxLength: ICP_FIELD_LIMITS.location }}
              helperText={`${formData.location?.length || 0}/${ICP_FIELD_LIMITS.location} characters`}
            />

            <TextField
              name="description"
              label="Brief Overview"
              value={formData.description}
              onChange={handleInputChange}
              multiline
              rows={4}
              fullWidth
              placeholder="Provide a brief overview of your ideal customer profile"
              inputProps={{ maxLength: ICP_FIELD_LIMITS.description }}
              helperText={`${formData.description?.length || 0}/${ICP_FIELD_LIMITS.description} characters`}
            />
          </Box>
        </TabPanel>

        {/* Organization Details Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6">
              {formData.businessProfile?.icpType === "b2c" ? "Consumer Profile" : "Business Profile"}
            </Typography>

            <TextField
              label={formData.businessProfile?.icpType === "b2c" ? "Interest Areas & Categories" : "Primary Industries & Sub-sectors"}
              value={formData.businessProfile?.industries.join(", ")}
              onChange={(e) =>
                handleArrayInput("businessProfile", "industries", e)
              }
              fullWidth
              multiline
              rows={2}
              placeholder={formData.businessProfile?.icpType === "b2c"
                ? "Enter interests separated by commas (e.g., Fitness, Technology, Home decor)"
                : "Enter industries separated by commas (e.g., Hospitality, High-end retail, Professional services)"}
              inputProps={{ maxLength: ICP_FIELD_LIMITS.arrayText }}
              helperText={`${formData.businessProfile?.industries.join(", ").length || 0}/${ICP_FIELD_LIMITS.arrayText}`}
            />

            <TextField
              label={formData.businessProfile?.icpType === "b2c" ? "Demographic Details" : "Company Size Range"}
              value={formData.businessProfile?.companySizeRange}
              onChange={(e) =>
                handleNestedChange(
                  "businessProfile",
                  "companySizeRange",
                  e.target.value
                )
              }
              fullWidth
              placeholder={formData.businessProfile?.icpType === "b2c"
                ? "e.g., Ages 25-45, college educated, urban professionals"
                : "e.g., Small to mid-sized businesses with 10-100 employees"}
              inputProps={{ maxLength: ICP_FIELD_LIMITS.mediumText }}
              helperText={`${formData.businessProfile?.companySizeRange?.length || 0}/${ICP_FIELD_LIMITS.mediumText}`}
            />

            <TextField
              label={formData.businessProfile?.icpType === "b2c" ? "Income Range" : "Annual Revenue Range"}
              value={formData.businessProfile?.revenue}
              onChange={(e) =>
                handleNestedChange("businessProfile", "revenue", e.target.value)
              }
              fullWidth
              placeholder={formData.businessProfile?.icpType === "b2c"
                ? "e.g., Household income of $75,000-$150,000"
                : "e.g., Annual revenue of $1-15 million"}
              inputProps={{ maxLength: ICP_FIELD_LIMITS.mediumText }}
              helperText={`${formData.businessProfile?.revenue?.length || 0}/${ICP_FIELD_LIMITS.mediumText}`}
            />

            <TextField
              label="Geographic Considerations"
              value={formData.businessProfile?.geography}
              onChange={(e) =>
                handleNestedChange(
                  "businessProfile",
                  "geography",
                  e.target.value
                )
              }
              fullWidth
              multiline
              rows={2}
              placeholder="Enter geographic focus areas, regions, districts"
              inputProps={{ maxLength: ICP_FIELD_LIMITS.mediumText }}
              helperText={`${formData.businessProfile?.geography?.length || 0}/${ICP_FIELD_LIMITS.mediumText}`}
            />

            <TextField
              label={formData.businessProfile?.icpType === "b2c" ? "Key Consumer Characteristics" : "Key Business Characteristics"}
              value={formData.businessProfile?.businessCharacteristics.join(
                ", "
              )}
              onChange={(e) =>
                handleArrayInput(
                  "businessProfile",
                  "businessCharacteristics",
                  e
                )
              }
              fullWidth
              multiline
              rows={3}
              placeholder={formData.businessProfile?.icpType === "b2c"
                ? "Enter characteristics separated by commas (e.g., Health-conscious, Tech-savvy, Value-driven)"
                : "Enter characteristics separated by commas (e.g., Brand-conscious, Emphasis on customer experience)"}
              inputProps={{ maxLength: ICP_FIELD_LIMITS.arrayText }}
              helperText={`${formData.businessProfile?.businessCharacteristics.join(", ").length || 0}/${ICP_FIELD_LIMITS.arrayText}`}
            />

            <Typography variant="h6" sx={{ mt: 2 }}>
              {formData.businessProfile?.icpType === "b2c" ? "Lifestyle Context" : "Strategic Context"}
            </Typography>

            <TextField
              label={formData.businessProfile?.icpType === "b2c" ? "Life Stage" : "Growth Stage"}
              value={formData.strategicContext?.growthStage}
              onChange={(e) =>
                handleNestedChange(
                  "strategicContext",
                  "growthStage",
                  e.target.value
                )
              }
              fullWidth
              multiline
              rows={2}
              placeholder={formData.businessProfile?.icpType === "b2c"
                ? "e.g., Young professionals, new parents, empty nesters"
                : "e.g., Established businesses in expansion phase"}
              inputProps={{ maxLength: ICP_FIELD_LIMITS.mediumText }}
              helperText={`${formData.strategicContext?.growthStage?.length || 0}/${ICP_FIELD_LIMITS.mediumText}`}
            />

            <TextField
              label={formData.businessProfile?.icpType === "b2c" ? "Personal Priorities" : "Strategic Priorities"}
              value={formData.strategicContext?.priorities.join(", ")}
              onChange={(e) =>
                handleArrayInput("strategicContext", "priorities", e)
              }
              fullWidth
              multiline
              rows={2}
              placeholder={formData.businessProfile?.icpType === "b2c"
                ? "Enter priorities separated by commas (e.g., Work-life balance, Health & fitness, Family time)"
                : "Enter priorities separated by commas (e.g., Elevating brand experience, Differentiating from competitors)"}
              inputProps={{ maxLength: ICP_FIELD_LIMITS.arrayText }}
              helperText={`${formData.strategicContext?.priorities.join(", ").length || 0}/${ICP_FIELD_LIMITS.arrayText}`}
            />

            <TextField
              label={formData.businessProfile?.icpType === "b2c" ? "Personal Values & Lifestyle" : "Organizational Values & Culture"}
              value={formData.strategicContext?.values.join(", ")}
              onChange={(e) =>
                handleArrayInput("strategicContext", "values", e)
              }
              fullWidth
              multiline
              rows={2}
              placeholder={formData.businessProfile?.icpType === "b2c"
                ? "Enter values separated by commas (e.g., Sustainability, Authenticity, Convenience)"
                : "Enter values separated by commas (e.g., Quality-oriented, Relationship-focused)"}
              inputProps={{ maxLength: ICP_FIELD_LIMITS.arrayText }}
              helperText={`${formData.strategicContext?.values.join(", ").length || 0}/${ICP_FIELD_LIMITS.arrayText}`}
            />
          </Box>
        </TabPanel>

        {/* Challenges & Buying Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6">Challenges & Needs</Typography>

            <TextField
              label="Pain Points"
              value={formData.challenges?.painPoints.join(", ")}
              onChange={(e) => handleArrayInput("challenges", "painPoints", e)}
              fullWidth
              multiline
              rows={3}
              placeholder={formData.businessProfile?.icpType === "b2c"
                ? "Enter pain points separated by commas (e.g., Limited time, Overwhelming choices, Trust concerns)"
                : "Enter pain points separated by commas (e.g., Finding contractors who understand brand alignment, Managing complex projects)"}
              inputProps={{ maxLength: ICP_FIELD_LIMITS.arrayText }}
              helperText={`${formData.challenges?.painPoints.join(", ").length || 0}/${ICP_FIELD_LIMITS.arrayText}`}
            />

            <TextField
              label="Needs"
              value={formData.challenges?.needs.join(", ")}
              onChange={(e) => handleArrayInput("challenges", "needs", e)}
              fullWidth
              multiline
              rows={3}
              placeholder={formData.businessProfile?.icpType === "b2c"
                ? "Enter needs separated by commas (e.g., Convenience, Quality assurance, Personalized experience)"
                : "Enter needs separated by commas (e.g., End-to-end project management, Craftsman-level execution)"}
              inputProps={{ maxLength: ICP_FIELD_LIMITS.arrayText }}
              helperText={`${formData.challenges?.needs.join(", ").length || 0}/${ICP_FIELD_LIMITS.arrayText}`}
            />

            <Typography variant="h6" sx={{ mt: 2 }}>
              {formData.businessProfile?.icpType === "b2c" ? "Purchase Behavior" : "Buying Process"}
            </Typography>

            <TextField
              label={formData.businessProfile?.icpType === "b2c" ? "Key Influencers" : "Key Decision-makers"}
              value={
                Array.isArray(formData.buyingProcess?.decisionMakers)
                  ? formData.buyingProcess.decisionMakers.join(", ")
                  : ""
              }
              onChange={(e) =>
                handleArrayInput("buyingProcess", "decisionMakers", e)
              }
              fullWidth
              multiline
              rows={2}
              placeholder={formData.businessProfile?.icpType === "b2c"
                ? "Enter influencers separated by commas (e.g., Spouse/Partner, Friends, Online reviews)"
                : "Enter roles separated by commas (e.g., Business Owner, Chief Executive Officer, Operations Director)"}
              inputProps={{ maxLength: ICP_FIELD_LIMITS.arrayText }}
              helperText={`${(Array.isArray(formData.buyingProcess?.decisionMakers) ? formData.buyingProcess.decisionMakers.join(", ") : "").length || 0}/${ICP_FIELD_LIMITS.arrayText}`}
            />

            <TextField
              label={formData.businessProfile?.icpType === "b2c" ? "Purchase Criteria" : "Critical Evaluation Criteria"}
              value={formData.buyingProcess?.evaluationCriteria.join(", ")}
              onChange={(e) =>
                handleArrayInput("buyingProcess", "evaluationCriteria", e)
              }
              fullWidth
              multiline
              rows={3}
              placeholder={formData.businessProfile?.icpType === "b2c"
                ? "Enter criteria separated by commas (e.g., Price, Reviews, Brand reputation, Convenience)"
                : "Enter criteria separated by commas (e.g., Portfolio of similar projects, Expertise in specific building types)"}
              inputProps={{ maxLength: ICP_FIELD_LIMITS.arrayText }}
              helperText={`${formData.buyingProcess?.evaluationCriteria.join(", ").length || 0}/${ICP_FIELD_LIMITS.arrayText}`}
            />

            <TextField
              label={formData.businessProfile?.icpType === "b2c" ? "Common Hesitations" : "Typical Objections"}
              value={formData.buyingProcess?.objections.join(", ")}
              onChange={(e) =>
                handleArrayInput("buyingProcess", "objections", e)
              }
              fullWidth
              multiline
              rows={2}
              placeholder={formData.businessProfile?.icpType === "b2c"
                ? "Enter hesitations separated by commas (e.g., Price sensitivity, Need to compare options, Timing)"
                : "Enter objections separated by commas (e.g., Premium pricing, Timeline concerns)"}
              inputProps={{ maxLength: ICP_FIELD_LIMITS.arrayText }}
              helperText={`${formData.buyingProcess?.objections.join(", ").length || 0}/${ICP_FIELD_LIMITS.arrayText}`}
            />
          </Box>
        </TabPanel>

        {/* Personas Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6">
              {formData.businessProfile?.icpType === "b2c" ? "Customer Personas" : "Key Personas"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {formData.businessProfile?.icpType === "b2c"
                ? "Describe the different types of consumers you want to target"
                : "Describe the key decision-makers or influencers in your target customers' organization"}
            </Typography>

            {formData.personas &&
              formData.personas.length > 0 &&
              formData.personas.map((persona: any, index: number) => (
                <Paper elevation={1} key={index} sx={{ p: 2, mt: 2 }}>
                  <Typography variant="subtitle1">
                    {formData.businessProfile?.icpType === "b2c" ? `Consumer Type ${index + 1}` : `Persona ${index + 1}`}
                  </Typography>

                  <TextField
                    label={formData.businessProfile?.icpType === "b2c" ? "Persona Name" : "Persona Name/Title"}
                    value={persona.name}
                    onChange={(e) => {
                      const updatedPersonas = [...formData.personas];
                      updatedPersonas[index].name = e.target.value;
                      setFormData({ ...formData, personas: updatedPersonas });
                    }}
                    fullWidth
                    sx={{ mt: 1 }}
                    placeholder={formData.businessProfile?.icpType === "b2c"
                      ? "e.g., The Busy Professional, The Budget-Conscious Parent"
                      : "e.g., Restaurant Owner/Developer"}
                    inputProps={{ maxLength: ICP_FIELD_LIMITS.shortText }}
                    helperText={`${persona.name?.length || 0}/${ICP_FIELD_LIMITS.shortText}`}
                  />

                  <TextField
                    label={formData.businessProfile?.icpType === "b2c" ? "Demographics & Lifestyle" : "Professional Role"}
                    value={persona.role}
                    onChange={(e) => {
                      const updatedPersonas = [...formData.personas];
                      updatedPersonas[index].role = e.target.value;
                      setFormData({ ...formData, personas: updatedPersonas });
                    }}
                    fullWidth
                    sx={{ mt: 1 }}
                    placeholder={formData.businessProfile?.icpType === "b2c"
                      ? "e.g., 30-40 year old urban professional, dual income household"
                      : "e.g., Experienced restaurateur with 5-15 years in the industry"}
                    inputProps={{ maxLength: ICP_FIELD_LIMITS.mediumText }}
                    helperText={`${persona.role?.length || 0}/${ICP_FIELD_LIMITS.mediumText}`}
                  />

                  <TextField
                    label={formData.businessProfile?.icpType === "b2c" ? "Consumer Profile" : "Professional Profile"}
                    value={persona.profile}
                    onChange={(e) => {
                      const updatedPersonas = [...formData.personas];
                      updatedPersonas[index].profile = e.target.value;
                      setFormData({ ...formData, personas: updatedPersonas });
                    }}
                    fullWidth
                    multiline
                    rows={2}
                    sx={{ mt: 1 }}
                    placeholder={formData.businessProfile?.icpType === "b2c"
                      ? "Describe their lifestyle, interests, and shopping habits"
                      : "Describe their background, experience, and responsibilities"}
                    inputProps={{ maxLength: ICP_FIELD_LIMITS.longText }}
                    helperText={`${persona.profile?.length || 0}/${ICP_FIELD_LIMITS.longText}`}
                  />

                  <TextField
                    label={formData.businessProfile?.icpType === "b2c" ? "Pain Points & Frustrations" : "Daily Challenges"}
                    value={persona.challenges.join(", ")}
                    onChange={(e) => {
                      const updatedPersonas = [...formData.personas];
                      updatedPersonas[index].challenges = e.target.value
                        .split(",")
                        .map((item) => item.trim());
                      setFormData({ ...formData, personas: updatedPersonas });
                    }}
                    fullWidth
                    multiline
                    rows={2}
                    sx={{ mt: 1 }}
                    placeholder={formData.businessProfile?.icpType === "b2c"
                      ? "Enter frustrations separated by commas (e.g., Lack of time, Hard to find quality)"
                      : "Enter challenges separated by commas"}
                    inputProps={{ maxLength: ICP_FIELD_LIMITS.arrayText }}
                    helperText={`${persona.challenges.join(", ").length || 0}/${ICP_FIELD_LIMITS.arrayText}`}
                  />

                  <TextField
                    label={formData.businessProfile?.icpType === "b2c" ? "Buying Motivations" : "Key Motivations & Concerns"}
                    value={persona.motivations.join(", ")}
                    onChange={(e) => {
                      const updatedPersonas = [...formData.personas];
                      updatedPersonas[index].motivations = e.target.value
                        .split(",")
                        .map((item) => item.trim());
                      setFormData({ ...formData, personas: updatedPersonas });
                    }}
                    fullWidth
                    multiline
                    rows={2}
                    sx={{ mt: 1 }}
                    placeholder={formData.businessProfile?.icpType === "b2c"
                      ? "Enter motivations separated by commas (e.g., Convenience, Status, Value for money)"
                      : "Enter motivations separated by commas"}
                    inputProps={{ maxLength: ICP_FIELD_LIMITS.arrayText }}
                    helperText={`${persona.motivations.join(", ").length || 0}/${ICP_FIELD_LIMITS.arrayText}`}
                  />

                  {index > 0 && (
                    <Button
                      color="error"
                      sx={{ mt: 1 }}
                      onClick={() => {
                        const updatedPersonas = [...formData.personas];
                        updatedPersonas.splice(index, 1);
                        setFormData({ ...formData, personas: updatedPersonas });
                      }}
                    >
                      Remove Persona
                    </Button>
                  )}
                </Paper>
              ))}

            <Button
              variant="outlined"
              sx={{ mt: 1 }}
              onClick={() => {
                const updatedPersonas = [
                  ...formData.personas,
                  {
                    name: "",
                    role: "",
                    profile: "",
                    challenges: [],
                    motivations: [],
                  },
                ];
                setFormData({ ...formData, personas: updatedPersonas });
              }}
            >
              Add Another Persona
            </Button>
          </Box>
        </TabPanel>

        {/* Go-to-Market Tab */}
        <TabPanel value={tabValue} index={4}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6">Go-to-Market Recommendations</Typography>
            <Typography variant="body2" color="textSecondary">
              Provide insights on the most effective ways to reach and engage
              your target customers
            </Typography>

            <TextField
              label="Most Effective Marketing Channels"
              value={formData.goToMarket?.channels.join(", ")}
              onChange={(e) => handleArrayInput("goToMarket", "channels", e)}
              fullWidth
              multiline
              rows={2}
              placeholder="Enter channels separated by commas (e.g., Industry-specific events, Referral partnerships)"
              inputProps={{ maxLength: ICP_FIELD_LIMITS.arrayText }}
              helperText={`${formData.goToMarket?.channels.join(", ").length || 0}/${ICP_FIELD_LIMITS.arrayText}`}
            />

            <TextField
              label="Key Messaging Themes"
              value={formData.goToMarket?.messaging.join(", ")}
              onChange={(e) => handleArrayInput("goToMarket", "messaging", e)}
              fullWidth
              multiline
              rows={2}
              placeholder="Enter messaging themes separated by commas (e.g., Craftsmanship expertise, Problem-solving approach)"
              inputProps={{ maxLength: ICP_FIELD_LIMITS.arrayText }}
              helperText={`${formData.goToMarket?.messaging.join(", ").length || 0}/${ICP_FIELD_LIMITS.arrayText}`}
            />

            <TextField
              label="Content Opportunities"
              value={formData.goToMarket?.contentStrategies.join(", ")}
              onChange={(e) =>
                handleArrayInput("goToMarket", "contentStrategies", e)
              }
              fullWidth
              multiline
              rows={3}
              placeholder="Enter content ideas separated by commas (e.g., Before/after case studies, Client testimonials)"
              inputProps={{ maxLength: ICP_FIELD_LIMITS.arrayText }}
              helperText={`${formData.goToMarket?.contentStrategies.join(", ").length || 0}/${ICP_FIELD_LIMITS.arrayText}`}
            />
          </Box>
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <LoadingButton 
          onClick={handleSubmit} 
          color="primary" 
          variant="contained"
          loading={loading}
        >
          {existingData ? "Update" : "Create"} Profile
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
